import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, ChevronDown, Sparkles } from "lucide-react";
import { LANDING_FEATURES, getFeatureBySlug } from "../utils/landingFeatures";
import LandingFeatureCard, { LandingLeadCard } from "../components/LandingProductCard";
import {
  HeroDealPreview,
  HighlightsStrip,
  LANDING_DISPLAY,
} from "../components/LandingMarketing";
import { track } from "../utils/analytics";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import useSeo from "../hooks/useSeo";
import { featureSeo } from "../seo/pages";
import { breadcrumbJsonLd, faqJsonLd } from "../seo/site";

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full py-5 flex items-center justify-between gap-4 text-left group"
      >
        <span className="font-semibold text-slate-900 group-hover:text-violet-700 transition-colors">
          {q}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
            open ? "rotate-180 text-violet-600" : ""
          }`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ${
          open ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-slate-600 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

const HERO_PREVIEW_LIMIT = 2;
const PREVIEW_LABEL = "Find deals like these";

export default function FeaturePage() {
  useEnforceLightMode();
  const { slug } = useParams<{ slug: string }>();
  const feature = slug ? getFeatureBySlug(slug) : undefined;

  useSeo(
    feature
      ? {
          ...featureSeo(feature),
          jsonLd: [
            breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: feature.navLabel }]),
            ...(feature.faq?.length ? [faqJsonLd(feature.faq)] : []),
          ],
        }
      : null,
  );

  useEffect(() => {
    if (feature) {
      track("feature_page_viewed", { feature: feature.slug });
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [feature]);

  if (!feature) return <Navigate to="/" replace />;

  const Icon = feature.icon;
  const otherFeatures = LANDING_FEATURES.filter((f) => f.slug !== feature.slug);
  const heroPreviewProducts = feature.products.slice(0, HERO_PREVIEW_LIMIT);

  const renderCard = (product: (typeof feature.products)[number], i: number) => (
    <LandingFeatureCard
      key={`${product.upc}-${i}`}
      product={product}
      style={feature.cardStyle}
      hottest={feature.cardStyle === "lead" && i === 0}
    />
  );

  const gridClass =
    feature.cardStyle === "lead"
      ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 justify-items-center"
      : "grid md:grid-cols-2 gap-4 sm:gap-5";

  const heroPreview =
    feature.heroIllustration ? (
      <div className="max-w-[280px] sm:max-w-[300px] lg:max-w-[320px] ml-auto rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_-15px_rgba(89,7,194,0.35)]">
        <img src={feature.heroIllustration} alt="" className="w-full h-auto block" />
      </div>
    ) : feature.cardStyle === "lead" ? (
      <HeroDealPreview label={PREVIEW_LABEL}>
        <div className="flex flex-col items-center gap-3">
          {heroPreviewProducts.map((p, i) => (
            <LandingLeadCard key={p.upc} product={p} hottest={i === 0} />
          ))}
        </div>
      </HeroDealPreview>
    ) : (
      <HeroDealPreview label={PREVIEW_LABEL} products={heroPreviewProducts} />
    );

  return (
    <div className={`min-h-screen bg-white ${LANDING_DISPLAY} text-slate-900`}>
      {/* HERO */}
      <section className="relative bg-[#0B0F14] text-white overflow-x-hidden">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#5907c2]/15 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-20 sm:pb-24">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All products
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#5907c2]/15 border border-[#d131f3]/30 text-violet-200 px-3 py-1.5 rounded-full text-xs font-semibold mb-5">
                <Icon className="w-3.5 h-3.5 text-[#d131f3]" />
                <span className="uppercase tracking-wider">{feature.heroEyebrow}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold leading-[1.08] text-white">
                {feature.heroTitle}
                <br />
                <span className="text-[#d131f3]">{feature.heroItalic}</span>
              </h1>

              <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                {feature.heroSubtitle}
              </p>

              <div className="mt-8 flex justify-center lg:justify-start">
                <Link
                  to="/get-started"
                  onClick={() =>
                    track("feature_cta_clicked", { feature: feature.slug, location: "hero" })
                  }
                  className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#5907c2] to-[#9d24eb] hover:from-[#6d28d9] hover:to-[#d131f3] text-white px-8 h-12 rounded-xl font-bold text-base transition-all w-full sm:w-auto"
                >
                  {feature.ctaLabel}
                  <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="hidden lg:block max-w-sm xl:max-w-md mx-auto lg:mx-0 lg:ml-auto w-full">
              {heroPreview}
            </div>
          </div>

          <div className="mt-10 lg:hidden max-w-md mx-auto w-full">{heroPreview}</div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 sm:py-28 bg-[#FAFAF8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-bold text-violet-600 uppercase tracking-widest">
              Why it works
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900">
              Built for the way you actually shop.
            </h2>
          </div>

          <HighlightsStrip />

          <div className="grid md:grid-cols-3 gap-6">
            {feature.benefits.map((b, i) => {
              const BIcon = b.icon;
              return (
                <div
                  key={i}
                  className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:border-violet-200 hover:shadow-md transition-all"
                >
                  <div
                    className={`flex items-center justify-center w-11 h-11 rounded-xl ${feature.iconBg} text-white mb-4`}
                  >
                    <BIcon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-[15px]">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LIVE PREVIEW */}
      {feature.products.length > 0 && (
        <section className="py-20 sm:py-28 bg-white border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <span className="text-xs font-bold text-violet-600 uppercase tracking-widest">
                A peek inside
              </span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900">
                {feature.cardStyle === "lead"
                  ? "Real leads members are flipping right now."
                  : feature.cardStyle === "tracker"
                    ? "Real alerts firing right now."
                    : "Real deals members are finding right now."}
              </h2>
              <p className="mt-4 text-lg text-slate-600">Sample data from 7+ retailers.</p>
            </div>
            <div className={gridClass}>{feature.products.map((p, i) => renderCard(p, i))}</div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-28 bg-[#FAFAF8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold text-violet-600 uppercase tracking-widest">
              How it works
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900">
              Three steps. Under a minute.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {feature.steps.map((s, i) => (
              <div key={i} className="flex md:flex-col gap-4">
                <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-[#5907c2] text-white">
                  <span className="text-sm font-bold">{i + 1}</span>
                </div>
                <div>
                  <span className="text-[11px] font-bold text-violet-600 uppercase tracking-widest">
                    Step {i + 1}
                  </span>
                  <h3 className="mt-1 text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {feature.faq && feature.faq.length > 0 && (
        <section className="py-20 sm:py-24 bg-white border-t border-slate-100">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-bold text-violet-600 uppercase tracking-widest">
                FAQ
              </span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900">
                Quick answers.
              </h2>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 px-6 sm:px-8">
              {feature.faq.map((f) => (
                <FAQItem key={f.q} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OTHER FEATURES */}
      <section className="py-20 sm:py-28 bg-[#FAFAF8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold text-violet-600 uppercase tracking-widest">
              Explore more
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900">
              The rest of the toolkit.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherFeatures.map((f) => {
              const FIcon = f.icon;
              return (
                <Link
                  key={f.slug}
                  to={`/features/${f.slug}`}
                  onClick={() =>
                    track("feature_cross_link_clicked", { from: feature.slug, to: f.slug })
                  }
                  className="group flex gap-5 rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-300 hover:shadow-lg hover:shadow-violet-500/5 transition-all"
                >
                  <div
                    className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl ${f.iconBg} text-white`}
                  >
                    <FIcon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 group-hover:text-violet-700 transition-colors">
                      {f.navLabel}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed line-clamp-2">
                      {f.navDescription}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-violet-600">
                      Learn more
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-violet-100 border border-violet-200 text-violet-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Ready when you are
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-slate-900">
            Start finding deals
            <br />
            <span className="text-[#5907c2]">in the next 60 seconds.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto">
            Setup takes under two minutes.
          </p>
          <Link
            to="/get-started"
            onClick={() =>
              track("feature_cta_clicked", { feature: feature.slug, location: "bottom" })
            }
            className="mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-[#5907c2] to-[#9d24eb] hover:from-[#6d28d9] hover:to-[#d131f3] text-white px-8 h-14 rounded-xl font-bold text-base transition-all"
          >
            {feature.ctaLabel}
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
